import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router";
import { Bell } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth";

import { NotificationList } from "@/features/notifications/components/notification-list";
import { useUnreadCount } from "@/features/notifications/hooks";

const navItems = [
  { href: "/assistant", label: "Assistant" },
  { href: "/catalog", label: "Catalog" },
  { href: "/ingest", label: "Scan" },
  { href: "/neighbors", label: "Neighbors" },
];

export function SiteHeader() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  const [linkCopied, setLinkCopied] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);

  const { data: unreadCount = 0 } = useUnreadCount();

  // Close menus on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuOpen(false);
  }, []);

  if (!user) return null;

  return (
    <header className="hidden border-b-2 bg-background lg:block">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4">
        <div className="flex items-center gap-6">
          <Link to="/assistant" className="font-display text-lg text-secondary-foreground">
            Chamfer
          </Link>
          <nav className="flex items-center">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  "-mb-[2px] border-b-2 px-4 py-3 text-sm font-medium transition-colors duration-200",
                  pathname.startsWith(item.href)
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                )}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>

        <div className="ml-auto flex items-center gap-2">
          {/* Notification bell */}
          <div className="relative" ref={notifRef}>
            <button
              type="button"
              onClick={() => {
                setNotifOpen(!notifOpen);
              }}
              className="text-muted-foreground hover:text-foreground relative flex items-center justify-center rounded-md p-1.5"
              aria-label="Notifications"
            >
              <Bell className="h-4 w-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>

            {notifOpen && (
              <div className="bg-popover border-border absolute top-full right-0 z-50 mt-1 w-72 overflow-hidden rounded-md border shadow-md">
                <NotificationList
                  onClose={() => {
                    setNotifOpen(false);
                  }}
                />
              </div>
            )}
          </div>

          {/* Account menu */}
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => {
                setMenuOpen(!menuOpen);
              }}
              className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 text-sm"
            >
              {user.name ?? "Account"}
            </button>

            {menuOpen && (
              <div className="bg-popover border-border absolute top-full right-0 z-50 mt-1 w-64 overflow-hidden rounded-md border shadow-md">
                <Link
                  to="/settings"
                  onClick={handleMenuClose}
                  className="hover:bg-muted block px-3 py-2 text-sm"
                >
                  Settings
                </Link>
                {user.slug && (
                  <button
                    type="button"
                    onClick={() => {
                      const url = `${window.location.origin}/n/${user.slug}`;
                      void navigator.clipboard.writeText(url);
                      setLinkCopied(true);
                      setTimeout(() => {
                        setLinkCopied(false);
                        setMenuOpen(false);
                      }, 1500);
                    }}
                    className="hover:bg-muted w-full px-3 py-2 text-left text-sm"
                  >
                    {linkCopied ? "Link copied!" : "Share profile"}
                  </button>
                )}
                <button
                  type="button"
                  onClick={logout}
                  className="hover:bg-muted w-full border-t px-3 py-2 text-left text-sm"
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
